import { Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { ComponentCanDeactivate } from '@extensions/component-can-deactivate';
import { CanDeactivateGuard } from '@guards/can-deactivate-guard/can-deactivate-guard';

/**
 * Service that maintains a registry of active components implementing ComponentCanDeactivate.
 * Allows checking CanDeactivate guard before same-route reloads without navigating away.
 */
@Injectable({
  providedIn: 'root',
})
export class ComponentRegistryService {
  private componentRegistry = new Map<string, ComponentCanDeactivate>();
  private static instance: ComponentRegistryService | null = null;

  constructor(
    private router: Router,
    @Optional() private canDeactivateGuard: CanDeactivateGuard,
  ) {
    // Store static reference for components that can't inject services
    ComponentRegistryService.instance = this;
  }

  /**
   * Gets the singleton instance of ComponentRegistryService.
   * Used by base classes that can't inject services directly.
   */
  static getInstance(): ComponentRegistryService | null {
    return ComponentRegistryService.instance;
  }

  /**
   * Gets the current route URL from the Router.
   * Used by base classes that can't inject Router directly.
   */
  getCurrentRoute(): string {
    return this.router.url;
  }

  /**
   * Registers a component with its current route.
   * Components are keyed by normalized route path (without query params).
   *
   * @param component - Component implementing ComponentCanDeactivate
   * @param route - Current route path (will be normalized)
   */
  register(component: ComponentCanDeactivate, route: string): void {
    const normalizedRoute = this.normalizeRoute(route);
    this.componentRegistry.set(normalizedRoute, component);
  }

  /**
   * Unregisters a component from the registry.
   *
   * @param component - Component to unregister
   */
  unregister(component: ComponentCanDeactivate): void {
    // Find and remove the component from registry
    for (const [route, registeredComponent] of this.componentRegistry.entries()) {
      if (registeredComponent === component) {
        this.componentRegistry.delete(route);
        break;
      }
    }
  }

  /**
   * Gets the component for a specific route.
   *
   * @param route - Route path (will be normalized)
   * @returns Component instance or null if not found
   */
  getComponentForRoute(route: string): ComponentCanDeactivate | null {
    const normalizedRoute = this.normalizeRoute(route);
    return this.componentRegistry.get(normalizedRoute) || null;
  }

  /**
   * Checks if navigation is allowed for the current route by checking CanDeactivate guard.
   * Returns a Promise that resolves to true if navigation is allowed, false if cancelled.
   *
   * @param route - Route path to check (defaults to current router URL)
   * @returns Promise<boolean> - true if navigation allowed, false if user cancelled
   */
  async checkCanDeactivate(route?: string): Promise<boolean> {
    const routeToCheck = route || this.router.url;
    const component = this.getComponentForRoute(routeToCheck);

    if (!component) {
      // No component registered for this route, allow navigation
      return true;
    }

    // If guard is not available (e.g., in tests), allow navigation
    if (!this.canDeactivateGuard) {
      return true;
    }

    try {
      // Check the guard (already returns a Promise)
      const canDeactivate = await this.canDeactivateGuard.checkComponent(component);
      return canDeactivate;
    } catch (error) {
      // If guard check fails, allow navigation (fail open)
      console.error('[ComponentRegistry] Error checking CanDeactivate guard:', error);
      return true;
    }
  }

  /**
   * Normalizes a route by removing query parameters and fragments.
   * This ensures routes with _reload parameter match correctly.
   *
   * @param route - Route path to normalize
   * @returns Normalized route path
   */
  private normalizeRoute(route: string): string {
    // Remove query params and fragments for route matching
    return route.split('?')[0].split('#')[0];
  }
}
